import React, { useCallback, useEffect, useRef, useState } from 'react';
import { message, Modal, Spin, Upload } from 'antd';
import { InboxOutlined, PlayCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  CellValueChangedEvent,
  themeQuartz,
  ModuleRegistry,
  AllCommunityModule,
} from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import { ENV } from '../../../config';
import { useParams } from 'react-router-dom';
import { useRouter } from '../../../core/hooks/useRouter';
import {
  workspaceService,
  ProjectWorkspace as WorkspaceData,
  WorkspaceCalculation,
  WorkspaceSheet,
  WorkspaceCell,
} from './WorkspaceService';
import { projectService, CalculationJob, ProjectReadiness } from '../Project/ProjectService';
import { usePartnershipContext } from '../../../core/context/PartnershipContext';
import entityService from '../../EntityView/services/EntityService';
import styles from './ProjectWorkspace.module.css';

const gridTheme = themeQuartz.withParams({
  backgroundColor: 'transparent',
  foregroundColor: '#e2e8f0',
  borderColor: 'rgba(255,255,255,0.08)',
  oddRowBackgroundColor: 'rgba(255,255,255,0.02)',
  headerBackgroundColor: 'rgba(26,39,68,0.80)',
  headerTextColor: '#94a3b8',
  rowHoverColor: 'rgba(242,101,34,0.08)',
  accentColor: '#F26522',
  fontFamily: 'Inter, sans-serif',
  fontSize: 13,
});

const outputGridTheme = themeQuartz.withParams({
  backgroundColor: 'rgba(15,8,0,0.95)',
  foregroundColor: '#e2d5c0',
  borderColor: 'rgba(242,101,34,0.15)',
  oddRowBackgroundColor: 'rgba(242,101,34,0.04)',
  headerBackgroundColor: 'rgba(40,18,0,0.90)',
  headerTextColor: '#F26522',
  rowHoverColor: 'rgba(242,101,34,0.08)',
  accentColor: '#F26522',
  fontFamily: 'Inter, sans-serif',
  fontSize: 13,
});

// CALC-chain sheets (R1–R4 style intermediate steps) get their own amber theme so they read
// as distinct from both editable INPUT sheets and final OUTPUT sheets in the sidebar/grid.
const calcGridTheme = themeQuartz.withParams({
  backgroundColor: 'rgba(20,16,0,0.90)',
  foregroundColor: '#e8dcc0',
  borderColor: 'rgba(245,158,11,0.18)',
  oddRowBackgroundColor: 'rgba(245,158,11,0.04)',
  headerBackgroundColor: 'rgba(46,34,0,0.90)',
  headerTextColor: '#F59E0B',
  rowHoverColor: 'rgba(245,158,11,0.08)',
  accentColor: '#F59E0B',
  fontFamily: 'Inter, sans-serif',
  fontSize: 13,
});

ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule]);
LicenseManager.setLicenseKey(ENV.AG_GRID_API_KEY || '');

const workspaceDefaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  filter: true,
  enableRowGroup: true,
  enablePivot: true,
  enableValue: true,
  minWidth: 80,
  maxWidth: 400,
};

const workspaceStatusBar = {
  statusPanels: [
    { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' as const },
    { statusPanel: 'agTotalRowCountComponent', align: 'center' as const },
    { statusPanel: 'agAggregationComponent' },
  ],
};

type RowData = Record<string, string>;

// Template scaffolding row labels (found in the "Main Header" column) that should never
// render as real data — see CLAUDE.md BUG-5/BUG-6/BA-BUG-3 marker-row convention.
const MARKER_ROW_VALUES = new Set([
  'hidden row', 'sample row', 'help text', 'top header',
  'instruction', 'main header', 'row reference',
  'column reference', 'reference - sheet',
]);

function isMainHeaderKey(key: string): boolean {
  return key.trim().toLowerCase() === 'main header';
}

// A sheet's "Main Header" column marks scaffolding rows (Instruction/Reference/Hidden Row/
// Sample Row placeholders). But loop-expanded OUTPUT rows (see CLAUDE.md BUG-6) inherit that
// same label as a side effect of SpreadsheetGear's row-copy, even though they carry real
// per-partner results — confirmed live on project 9066's R5 Dep Allocation, where every
// genuine result row still reads Main Header="Hidden Row". So a marker value alone isn't
// enough: only drop the row if it has no other data — a real scaffolding row is empty
// everywhere except that one label.
function isMarkerRow(row: RowData): boolean {
  const key = Object.keys(row).find(isMainHeaderKey);
  if (!key) return false;
  const value = row[key];
  if (!value || !MARKER_ROW_VALUES.has(value.toString().toLowerCase().trim())) return false;
  return !Object.entries(row).some(([k, v]) => {
    if (k === key || k === '__source') return false;
    return v !== null && v !== undefined && v.toString().trim() !== '';
  });
}

function sheetToRows(sheet: WorkspaceSheet): RowData[] {
  return (sheet.rows || [])
    .map((row, idx) => {
      const obj: RowData = {};
      (row as any[]).forEach((cell: any) => {
        const colName = sheet.columns?.[cell.columnIndex]?.name || `col_${cell.columnIndex}`;
        obj[colName] = cell.value ?? '';
      });
      obj.__source = sheet.rowSources?.[idx] ?? 'manual';
      return obj;
    })
    .filter((row) => !isMarkerRow(row));
}

function parseOutputRows(sheet: WorkspaceSheet): RowData[] {
  return (sheet.outputRows ?? [])
    .map((r) => {
      const parsed = typeof r.data === 'string' ? JSON.parse(r.data) : r.data;
      return parsed as RowData;
    })
    .filter((row) => !isMarkerRow(row));
}

function rowsToPayload(rowData: RowData[], colCount: number): WorkspaceCell[][] {
  return rowData.map((row) => {
    const cells: WorkspaceCell[] = [];
    for (let i = 0; i < colCount; i++) {
      cells.push({ columnIndex: i, value: row[`col_${i}`] ?? '' });
    }
    return cells;
  });
}

// Map trial-balance account types to INCOME_INPUT row labels
const INCOME_CATEGORY_MAP: Record<string, string> = {
  ordinary: 'Ordinary Business Income',
  interest: 'Interest Income',
  'capital gain': 'Net LT Capital Gain',
  'long-term capital gain': 'Net LT Capital Gain',
  ltcg: 'Net LT Capital Gain',
};

function mapAccountTypeToCategory(accountType: string): string {
  const lower = (accountType ?? '').toLowerCase();
  for (const [key, label] of Object.entries(INCOME_CATEGORY_MAP)) {
    if (lower.includes(key)) return label;
  }
  return '';
}

function isOutputSheet(sheet: WorkspaceSheet): boolean {
  return sheet.isOutput === true || (!sheet.isInput && sheet.sheetName.toUpperCase().includes('OUTPUT'));
}

// CALC-chain sheets (e.g. R1–R4 in the Depreciation template) are the intermediate audit-trail
// steps between INPUT and OUTPUT — the backend doesn't send a dedicated role flag for these yet
// (WorkspaceSheetDto only has isInput/isOutput), so a sheet that is neither is the CALC signal.
function isCalcSheet(sheet: WorkspaceSheet): boolean {
  return !isOutputSheet(sheet) && !sheet.isInput;
}

// Plain-English descriptions for known CALC-chain column headers (Depreciation template's
// R1–R4 sheets) — these sheets are read-only intermediate results, so the header tooltip is
// the only place a reviewer can see what each column represents.
const CALC_COLUMN_DESCRIPTIONS: Record<string, string> = {
  'asset id': 'Identifier for the depreciable asset this row belongs to.',
  'asset description': 'Human-readable name of the asset.',
  'asset type': "Asset class used to look up the asset's depreciation method/rate.",
  method: 'Depreciation method applied to this asset (e.g. MACRS, straight-line).',
  convention: 'Depreciation convention governing the first-year deduction (e.g. half-year, mid-quarter).',
  life: 'Recovery period (in years) used to depreciate this asset.',
  'current year boy net tax basis': 'Net tax basis at the beginning of the current tax year.',
  'current year annual depreciation': 'Depreciation expense computed for the current tax year.',
  'current year eoy net tax basis': 'Net tax basis remaining at the end of the current tax year.',
  'sec. 704(c) layer id': 'Identifier for the Sec. 704(c) built-in gain/loss layer this row allocates.',
  'sec. 704(c) type': 'Type of Sec. 704(c) layer (e.g. contributed property, revaluation).',
  'current year boy net sec. 704(c) gain/loss': 'Remaining Sec. 704(c) built-in gain/loss at the start of the year.',
  'current year eoy net sec. 704(c) gain/loss': 'Remaining Sec. 704(c) built-in gain/loss at the end of the year.',
  'tax depreciation': 'Tax depreciation carried over from R1 Tax Depreciation for this asset.',
  'sec. 704(c) depreciation': 'Depreciation attributable to the Sec. 704(c) built-in gain/loss layer.',
  'sec. 704(b) depreciation': 'Book (Sec. 704(b)) depreciation for this asset, used for capital account maintenance.',
  'sec. 704(c) layer type': 'Type of Sec. 704(c) layer this basis-allocation row belongs to.',
  'net sec. 704(c) gain/loss': 'Remaining Sec. 704(c) built-in gain/loss being allocated across partners.',
  ratio: "Partner's Sec. 704(b) sharing ratio, used to allocate this layer's basis/cost recovery.",
  'net tax basis assigned': "Portion of the asset's net tax basis assigned to this partner.",
  'sec. 704(c) cost recovery': "Partner's share of cost recovery attributable to the Sec. 704(c) layer.",
  'tax cost recovery': "Partner's total tax cost recovery (depreciation) for this asset layer.",
  'sec. 704(b) cr': "Partner's Sec. 704(b) (book) cost recovery for this asset layer.",
};

function getCalcColumnTooltip(columnName: string): string {
  const key = (columnName ?? '').trim().toLowerCase();
  const yearMatch = key.match(/^year (\d+)$/);
  if (yearMatch) return `Depreciation amount for year ${yearMatch[1]} of this asset's recovery schedule.`;
  return CALC_COLUMN_DESCRIPTIONS[key]
    ?? 'Intermediate value computed in this calculation step — see the Excel template for the exact formula.';
}

// A readiness check's detail sometimes ends in "<summary>: name1, name2, name3" (see
// CLAUDE.md — ProjectService.GetProjectReadinessAsync folds per-CalcProfileItem names into
// one shared check's detail string). Split those into one bulleted line per item instead of
// a single run-on sentence; anything else renders unchanged.
function renderCheckDetail(icon: string, detail: string): React.ReactNode {
  const match = detail.match(/^(.*:)\s*(.+)$/);
  if (!match) return <>{icon} {detail}</>;
  const [, intro, namesPart] = match;
  const names = namesPart.split(',').map((n) => n.trim()).filter(Boolean);
  if (names.length <= 1) return <>{icon} {detail}</>;
  return (
    <>
      <div>{icon} {intro}</div>
      {names.map((n) => <div key={n} style={{ paddingLeft: 18 }}>• {n}</div>)}
    </>
  );
}

// Column width bounds by header-name convention, so wide text (partner/asset names, long
// descriptions) gets room while IDs/ratios/dates stay compact — actual width within these
// bounds is still resolved per-sheet by AG Grid's autoSizeAllColumns against real cell content.
function getColumnWidthBounds(colName: string): { minWidth: number; maxWidth: number } {
  const lower = (colName ?? '').toLowerCase();
  // "type" is included alongside name/description: sheets like Sys_MACRS carry an "Asset Type"
  // column whose values (e.g. "Office Furniture Fixtures and Equipment") are as long as any
  // description column.
  if (lower.includes('name') || lower.includes('description') || lower.includes('type')) {
    return { minWidth: 180, maxWidth: 320 };
  }
  if (lower.endsWith('id') || lower.includes(' id')) {
    return { minWidth: 80, maxWidth: 120 };
  }
  if (
    lower.includes('amount') || lower.includes('basis') || lower.includes('capital')
    || lower.includes('allocation') || lower.includes('ratio')
  ) {
    return { minWidth: 100, maxWidth: 180 };
  }
  if (lower.includes('date')) {
    return { minWidth: 100, maxWidth: 130 };
  }
  return { minWidth: 80, maxWidth: 200 };
}

// Shared numeric formatting by header-name convention (currency / percentage / year) — applied
// as a valueFormatter, so it only affects display; underlying cell values/editing are untouched.
function formatCellValue(value: unknown, headerName: string): string {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return String(value);

  const lower = (headerName ?? '').toLowerCase();

  if (
    lower.includes('basis') || lower.includes('capital') || lower.includes('allocation')
    || lower.includes('amount') || lower.includes('gain') || lower.includes('loss')
    || lower.includes('depreciation') || lower.includes('recovery')
  ) {
    return num.toLocaleString('en-US', {
      style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
  }

  if (
    lower.includes('ratio') || lower.includes('rate') || lower.includes('percent')
    || lower.includes(' sr') || lower.includes('relative')
  ) {
    return `${(num * 100).toFixed(4)}%`;
  }

  if (lower.includes('year') || lower.includes('life')) return num.toString();

  return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
}

export const ProjectWorkspace: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { go } = useRouter();
  const { selectedClient } = usePartnershipContext();

  const [workspace, setWorkspace] = useState<WorkspaceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeCalcId, setActiveCalcId] = useState<string | null>(null);
  const [selectedSheetIndex, setSelectedSheetIndex] = useState(0);
  const [gridRows, setGridRows] = useState<RowData[]>([]);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [workPapersLoading, setWorkPapersLoading] = useState(false);
  const [calcFading, setCalcFading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [runSuccess, setRunSuccess] = useState(false);
  const [completedJobs, setCompletedJobs] = useState(0);
  const [totalJobs, setTotalJobs] = useState(0);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [workspaceError, setWorkspaceError] = useState(false);
  const [pollingError, setPollingError] = useState(false);
  const [failedJobs, setFailedJobs] = useState<CalculationJob[]>([]);
  const [readiness, setReadiness] = useState<ProjectReadiness | null>(null);
  const [readinessLoading, setReadinessLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(
    () => localStorage.getItem('workspace-sidebar-collapsed') === 'true',
  );
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [calcPanelCollapsed, setCalcPanelCollapsed] = useState<boolean>(
    () => localStorage.getItem('workspace-calc-panel-collapsed') === 'true',
  );
  const [calcPanelHovered, setCalcPanelHovered] = useState(false);

  const gridRef = useRef<AgGridReact>(null);
  const gridApiRef = useRef<GridApi | null>(null);
  const recalcTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prefillAttemptedRef = useRef<Set<string>>(new Set());
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeCalcIdRef = useRef<string | null>(null);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('workspace-sidebar-collapsed', String(next));
      return next;
    });
  }, []);

  const toggleCalcPanel = useCallback(() => {
    setCalcPanelCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('workspace-calc-panel-collapsed', String(next));
      return next;
    });
  }, []);

  const fetchWorkspace = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setWorkspaceError(false);
    try {
      const data = await workspaceService.getWorkspace(projectId);
      setWorkspace(data);
      if (data.calculations.length > 0) {
        const searchParams = new URLSearchParams(window.location.search);
        const selectedCalcName = searchParams.get('calc');
        let targetCalc = data.calculations[0];
        if (selectedCalcName) {
          const match = data.calculations.find(
            (c) => c.standardCalculationName.toLowerCase() === selectedCalcName.toLowerCase(),
          );
          if (match) targetCalc = match;
        }
        setActiveCalcId(targetCalc.calcProfileItemId);
        let targetSheetIdx = 0;
        if (selectedCalcName) {
          const outputSheet = targetCalc.sheets.find((s) => isOutputSheet(s));
          if (outputSheet) targetSheetIdx = outputSheet.sheetIndex;
        }
        setSelectedSheetIndex(targetSheetIdx);
        const targetSheet = targetCalc.sheets.find((s) => s.sheetIndex === targetSheetIdx) ?? targetCalc.sheets[0];
        setGridRows(targetSheet ? (isOutputSheet(targetSheet) ? parseOutputRows(targetSheet) : sheetToRows(targetSheet)) : []);
      }
    } catch (err) {
      console.error('[CalcEngine] Workspace fetch failed:', err);
      setWorkspaceError(true);
      message.error('Failed to load workspace');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const fetchReadiness = useCallback(async () => {
    if (!projectId) return;
    setReadinessLoading(true);
    try {
      const data = await projectService.getReadiness(projectId);
      setReadiness(data);
    } catch (err) {
      console.error('[CalcEngine] Readiness fetch failed:', err);
      setReadiness(null);
    } finally {
      setReadinessLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchWorkspace(); }, [fetchWorkspace]);
  useEffect(() => { fetchReadiness(); }, [fetchReadiness]);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  useEffect(() => { activeCalcIdRef.current = activeCalcId; }, [activeCalcId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement
        || e.target instanceof HTMLTextAreaElement
      ) return;

      if (e.key === '[' && !e.shiftKey) {
        toggleSidebar();
      } else if (e.key === '[' && e.shiftKey) {
        e.preventDefault();
        toggleCalcPanel();
      } else if (e.key === '\\' && e.shiftKey) {
        e.preventDefault();
        setSidebarCollapsed(true);
        setCalcPanelCollapsed(true);
        localStorage.setItem('workspace-sidebar-collapsed', 'true');
        localStorage.setItem('workspace-calc-panel-collapsed', 'true');
      } else if (e.key === 'Escape') {
        setSidebarCollapsed(false);
        setCalcPanelCollapsed(false);
        localStorage.setItem('workspace-sidebar-collapsed', 'false');
        localStorage.setItem('workspace-calc-panel-collapsed', 'false');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar, toggleCalcPanel]);

  // Auto-fit columns to real cell content whenever the visible rows change (sheet switch,
  // calc switch, run completion) — bounded per column by getColumnWidthBounds via colDefs.
  useEffect(() => {
    if (!gridApiRef.current || gridRows.length === 0) return;
    const timer = setTimeout(() => {
      gridApiRef.current?.autoSizeAllColumns();
    }, 100);
    return () => clearTimeout(timer);
  }, [gridRows, selectedSheetIndex]);

  useEffect(() => {
    if (!runSuccess || !projectId) return;
    workspaceService.getWorkspace(projectId).then((data) => {
      setWorkspace(data);
      const currentCalcId = activeCalcIdRef.current;
      const currentCalc = data.calculations.find((c) => c.calcProfileItemId === currentCalcId);
      const outputSheet = currentCalc?.sheets.find((s) => isOutputSheet(s));
      if (outputSheet) {
        setSelectedSheetIndex(outputSheet.sheetIndex);
        setGridRows(parseOutputRows(outputSheet));
      }
    }).catch(() => {});
  }, [runSuccess, projectId]);

  const pollJobStatus = () => {
    if (!projectId) return;
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    setPollingError(false);
    pollIntervalRef.current = setInterval(async () => {
      try {
        const jobs: CalculationJob[] = await projectService.getJobs(projectId);
        const completed = jobs.filter((j: CalculationJob) => j.status === 'Completed').length;
        // Blocked jobs (an upstream dependency in this chain failed) are counted alongside
        // Failed/Error here — like a real failure, a Blocked job will never complete on its
        // own and the cascade has stopped making progress, so polling must not wait for it.
        const failed = jobs.filter(
          (j: CalculationJob) => j.status === 'Failed' || j.status === 'Error' || j.status === 'Blocked',
        );
        setCompletedJobs(completed);
        setTotalJobs(jobs.length);
        setFailedJobs(failed);
        if (completed + failed.length === jobs.length && jobs.length > 0) {
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          setIsRunning(false);
          if (failed.length > 0) setRunError(`${failed.length} calculation${failed.length > 1 ? 's' : ''} failed`);
          else setRunSuccess(true);
          fetchReadiness();
        }
      } catch (err) {
        console.error('[CalcEngine] Job polling failed:', err);
        setPollingError(true);
      }
    }, 2000);
  };

  const handleRunProject = async () => {
    if (!projectId) return;
    if (readiness && !readiness.isReadyToRun) return;
    setIsRunning(true);
    setRunError(null);
    setRunSuccess(false);
    setCompletedJobs(0);
    setTotalJobs(0);
    setFailedJobs([]);
    setShowProgressModal(true);
    try {
      const jobs = await projectService.runProject(projectId);
      setTotalJobs(jobs.length);
      pollJobStatus();
    } catch {
      setIsRunning(false);
      setRunError('Failed to trigger calculation. Please try again.');
    }
  };

  const activeCalc: WorkspaceCalculation | null = workspace?.calculations.find(
    (c) => c.calcProfileItemId === activeCalcId,
  ) ?? null;

  const activeSheet: WorkspaceSheet | null = activeCalc?.sheets[selectedSheetIndex] ?? null;

  // Pre-fill PARTNER_INPUT from partner entities when rows are empty
  const prefillPartnerRows = useCallback(async (calc: WorkspaceCalculation, clientId: string) => {
    const sheet = calc.sheets[0];
    if (!sheet || sheet.rows.length > 0) return;

    try {
      const entities = await entityService.getEntitiesByClient(clientId);
      const partners = entities.filter((e) => e.entityType === 'Partner');
      if (partners.length === 0) return;

      const partnerNameCol = sheet.columns.find((c) => {
        const n = c.name.toLowerCase();
        return n.includes('partner') || n.includes('name');
      });

      const rows: RowData[] = partners.map((p) => {
        const row: RowData = {};
        sheet.columns.forEach((col) => { row[`col_${col.columnIndex}`] = ''; });
        if (partnerNameCol) row[`col_${partnerNameCol.columnIndex}`] = p.name;
        return row;
      });

      setGridRows(rows);
    } catch (err) {
      console.error('[CalcEngine] Partner row prefill failed:', err);
    }
  }, []);

  // Pre-fill INCOME_INPUT from trial balance when rows are empty
  const prefillIncomeInputRows = useCallback(async (
    entityId: number,
    sheet: WorkspaceSheet,
  ) => {
    if (sheet.rows.length > 0) return;

    try {
      const tb = await workspaceService.getTrialBalance(entityId);
      if (!tb.rows.length) return;

      // Aggregate by income category
      const categoryTotals: Record<string, number> = {};
      for (const row of tb.rows) {
        const cat = mapAccountTypeToCategory(row.accountType);
        if (!cat) continue;
        categoryTotals[cat] = (categoryTotals[cat] ?? 0) + row.amount;
      }

      const categoryCol = sheet.columns.find((c) => {
        const n = c.name.toLowerCase();
        return n.includes('category') || n.includes('income') || n.includes('type') || c.columnIndex === 0;
      });
      const amountCol = sheet.columns.find((c) => {
        const n = c.name.toLowerCase();
        return n.includes('amount') || n.includes('total') || c.columnIndex === 1;
      });

      if (!categoryCol && !amountCol) return;

      const prefillRows: RowData[] = Object.entries(categoryTotals).map(([cat, total]) => {
        const row: RowData = {};
        sheet.columns.forEach((col) => { row[`col_${col.columnIndex}`] = ''; });
        if (categoryCol) row[`col_${categoryCol.columnIndex}`] = cat;
        if (amountCol) row[`col_${amountCol.columnIndex}`] = total.toFixed(2);
        return row;
      });

      if (prefillRows.length > 0) setGridRows(prefillRows);
    } catch (err) {
      console.error('[CalcEngine] Income input prefill failed:', err);
    }
  }, []);

  // Partner pre-fill trigger (sheet 0 only)
  useEffect(() => {
    if (!workspace || !selectedClient?.id || !activeCalcId || selectedSheetIndex !== 0) return;
    const key = `partner-${workspace.projectId}-${activeCalcId}`;
    if (prefillAttemptedRef.current.has(key)) return;
    const calc = workspace.calculations.find((c) => c.calcProfileItemId === activeCalcId);
    if (!calc) return;
    const sheet = calc.sheets[0];
    if (!sheet || sheet.rows.length > 0) return;
    prefillAttemptedRef.current.add(key);
    prefillPartnerRows(calc, selectedClient.id);
  }, [workspace, activeCalcId, selectedClient?.id, selectedSheetIndex, prefillPartnerRows]);

  // INCOME_INPUT pre-fill trigger
  useEffect(() => {
    if (!workspace || !activeCalcId) return;
    const sheet = activeCalc?.sheets[selectedSheetIndex];
    if (!sheet || !sheet.sheetName.toUpperCase().includes('INCOME_INPUT')) return;
    const key = `income-${workspace.projectId}-${activeCalcId}`;
    if (prefillAttemptedRef.current.has(key)) return;
    if (sheet.rows.length > 0) return;
    prefillAttemptedRef.current.add(key);
    prefillIncomeInputRows(workspace.entityId, sheet);
  }, [workspace, activeCalcId, selectedSheetIndex, activeCalc, prefillIncomeInputRows]);

  // Output tab fallback: legacy-convention templates (e.g. Redwood's K1_OUTPUT) never write
  // embedded project_input_row output rows — the Function App's legacy path only writes
  // calculation_result. When the active output sheet has no embedded rows, ask the
  // calculation_result-backed /workspace/output endpoint instead.
  useEffect(() => {
    if (!workspace || !activeCalcId || !projectId) return;
    const sheet = activeCalc?.sheets[selectedSheetIndex];
    if (!sheet || !isOutputSheet(sheet)) return;
    if ((sheet.outputRows ?? []).length > 0) return;
    const key = `output-fallback-${workspace.projectId}-${activeCalcId}-${selectedSheetIndex}`;
    if (prefillAttemptedRef.current.has(key)) return;
    prefillAttemptedRef.current.add(key);
    workspaceService.getOutputRows(projectId, activeCalc?.standardCalculationName)
      .then((res) => {
        const rows = res.sheets
          .flatMap((s) => s.rows.map((r) => (typeof r.data === 'string' ? JSON.parse(r.data) : r.data) as RowData))
          .filter((row) => !isMarkerRow(row));
        if (rows.length > 0) setGridRows(rows);
      })
      .catch((err) => console.error('[CalcEngine] Output fallback fetch failed:', err));
  }, [workspace, activeCalcId, selectedSheetIndex, activeCalc, projectId]);

  const handleCalcSelect = (calc: WorkspaceCalculation) => {
    if (calc.calcProfileItemId === activeCalcId) return;
    setCalcFading(true);
    setTimeout(() => {
      setActiveCalcId(calc.calcProfileItemId);
      setSelectedSheetIndex(0);
      const firstSheet = calc.sheets[0];
      setGridRows(firstSheet ? (isOutputSheet(firstSheet) ? parseOutputRows(firstSheet) : sheetToRows(firstSheet)) : []);
      setCalcFading(false);
    }, 150);
  };

  const handleSheetTabSelect = (sheetIdx: number) => {
    if (!activeCalc) return;
    setSelectedSheetIndex(sheetIdx);
    const sheet = activeCalc.sheets[sheetIdx];
    setGridRows(sheet ? (isOutputSheet(sheet) ? parseOutputRows(sheet) : sheetToRows(sheet)) : []);
  };

  const activeSheetIsOutput = activeSheet ? isOutputSheet(activeSheet) : false;
  const activeSheetIsCalc = activeSheet ? isCalcSheet(activeSheet) : false;

  let colDefs: ColDef[];
  if (activeSheetIsOutput) {
    // gridRows is the single source of truth here — populated from embedded outputRows when
    // present, or from the calculation_result fallback (see effect above) when they're empty.
    colDefs = gridRows.length > 0
      ? Object.keys(gridRows[0]).filter((key) => !isMainHeaderKey(key)).map((key) => {
          const { minWidth, maxWidth } = getColumnWidthBounds(key);
          return {
            field: key,
            headerName: key,
            editable: false,
            minWidth,
            maxWidth,
            enableRowGroup: true,
            enablePivot: true,
            enableValue: true,
            valueFormatter: (p) => formatCellValue(p.value, key),
            cellStyle: { backgroundColor: 'rgba(20,10,0,0.60)', color: '#F2A55E' } as Record<string, string>,
          };
        })
      : [];
  } else {
    colDefs = (activeSheet?.columns ?? []).filter((col) => !isMainHeaderKey(col.name || '')).map((col) => {
      const isFirstCol = col.columnIndex === 0;
      const truncatedFormula = col.formula
        ? (col.formula.length > 80 ? `${col.formula.slice(0, 80)}...` : col.formula)
        : null;
      const colLabel = col.name || `col_${col.columnIndex}`;
      const { minWidth, maxWidth } = getColumnWidthBounds(colLabel);
      return {
        field: colLabel,
        headerName: col.isInput ? col.name : `∑ ${col.name}`,
        headerTooltip: activeSheetIsCalc
          ? getCalcColumnTooltip(col.name)
          : (!col.isInput && truncatedFormula ? truncatedFormula : undefined),
        editable: col.isInput,
        minWidth,
        maxWidth,
        pinned: isFirstCol ? ('left' as const) : undefined,
        enableRowGroup: true,
        enablePivot: !col.isInput,
        enableValue: !col.isInput,
        tooltipValueGetter: col.isInput
          ? undefined
          : () => `Formula: ${col.formula || 'Calculated'}`,
        valueFormatter: (p) => formatCellValue(p.value, colLabel),
        cellStyle: col.isInput
          ? ({ backgroundColor: 'rgba(255,255,255,0.05)', color: '#fff' } as Record<string, string>)
          : ({ backgroundColor: 'rgba(100,100,120,0.2)', color: '#aaa', fontStyle: 'italic' } as Record<string, string>),
      };
    });
    const isMetadataColumns = colDefs.length > 0 &&
      (colDefs[0].headerName === 'Instruction' ||
       colDefs[0].field === 'Instruction');
    if (isMetadataColumns && gridRows.length > 0) {
      colDefs = Object.keys(gridRows[0])
        .filter((k) => k !== '__rowIndex' && k !== '__source' && !isMainHeaderKey(k))
        .map((key) => {
          const { minWidth, maxWidth } = getColumnWidthBounds(key);
          return {
            field: key,
            headerName: key,
            editable: activeSheet?.isInput ?? true,
            minWidth,
            maxWidth,
            enableRowGroup: true,
            enablePivot: true,
            enableValue: true,
            valueFormatter: (p) => formatCellValue(p.value, key),
            cellStyle: { backgroundColor: 'rgba(255,255,255,0.05)', color: '#fff' } as Record<string, string>,
          };
        });
    }

    if (activeSheet?.isInput && gridRows.length > 0) {
      colDefs = [
        ...colDefs,
        {
          field: '__source',
          headerName: 'Source',
          editable: false,
          width: 110,
          pinned: 'right' as const,
          sortable: true,
          filter: true,
          cellRenderer: (p: { value?: string }) => {
            const isUpload = p.value === 'upload';
            const label = isUpload ? 'Uploaded' : 'Manual';
            const bg = isUpload ? 'rgba(34,197,94,0.15)' : 'rgba(148,163,184,0.15)';
            const fg = isUpload ? '#22c55e' : '#94a3b8';
            return `<span style="font-size:10px;font-weight:600;padding:2px 8px;border-radius:10px;background:${bg};color:${fg};">${label}</span>`;
          },
        },
      ];
    }
  }

  const noRowsOverlay = '<div style="display:flex;flex-direction:column;align-items:center;gap:8px;padding:32px 16px;">'
    + '<span style="font-size:13px;color:rgba(255,255,255,0.40);">Upload partner data or click <strong>+ Add Row</strong> to get started</span>'
    + '<span style="font-size:20px;margin-top:4px;">↓</span>'
    + '</div>';

  const outputNoRowsOverlay = '<div style="display:flex;flex-direction:column;align-items:center;gap:8px;padding:32px 16px;">'
    + '<span style="font-size:20px;margin-top:4px;">⚡</span>'
    + '<span style="font-size:13px;color:rgba(242,101,34,0.70);">Run calculation to see results</span>'
    + '</div>';

  const calcNoRowsOverlay = '<div style="display:flex;flex-direction:column;align-items:center;gap:8px;padding:32px 16px;">'
    + '<span style="font-size:20px;margin-top:4px;">🔗</span>'
    + '<span style="font-size:13px;color:rgba(245,158,11,0.75);">Run the project to see intermediate calculation results</span>'
    + '</div>';

  const handleAddRow = () => {
    const emptyRow: RowData = {};
    (activeSheet?.columns ?? []).forEach((col) => {
      emptyRow[col.name || `col_${col.columnIndex}`] = '';
    });
    emptyRow.__source = 'manual';
    setGridRows((prev) => [emptyRow, ...prev]);
  };

  const handleCellValueChanged = (e: CellValueChangedEvent) => {
    const idx = e.rowIndex ?? 0;
    setGridRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [e.colDef.field as string]: e.newValue ?? '' };
      return next;
    });

    if (!projectId || !activeCalcId) return;

    if (recalcTimerRef.current) clearTimeout(recalcTimerRef.current);
    recalcTimerRef.current = setTimeout(async () => {
      const colIndexStr = (e.colDef.field as string).replace('col_', '');
      const colIndex = parseInt(colIndexStr, 10);
      setCalculating(true);
      try {
        const response = await workspaceService.recalculate(projectId, {
          calcProfileItemId: activeCalcId,
          rows: [{ rowIndex: idx, columnIndex: colIndex, value: e.newValue ?? '' }],
        });

        if (response.rows.length > 0) {
          setGridRows((prev) => {
            const next = [...prev];
            response.rows.forEach((cell) => {
              if (next[cell.rowIndex]) {
                next[cell.rowIndex] = {
                  ...next[cell.rowIndex],
                  [`col_${cell.columnIndex}`]: cell.value,
                };
              }
            });
            return next;
          });
        }
      } catch {
        // Silent — recalculation is best-effort
      } finally {
        setCalculating(false);
      }
    }, 600);
  };

  const handleSave = async () => {
    if (!projectId || !activeCalcId || !activeSheet) return;
    setSaving(true);
    try {
      await workspaceService.saveRows(projectId, {
        calcProfileItemId: activeCalcId,
        sheetIndex: selectedSheetIndex,
        rows: rowsToPayload(gridRows, activeSheet.columns.length),
      });
      message.success('Rows saved');
    } catch {
      message.error('Failed to save rows');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadTemplate = () => {
    if (!activeCalc?.templateVersionId) {
      message.warning('No template assigned to this calculation');
      return;
    }
    workspaceService.downloadTemplate(activeCalc.templateVersionId);
  };

  const handleUploadClick = () => {
    setUploadFile(null);
    setUploadModalOpen(true);
  };

  const handleDownloadWorkPapers = async () => {
    if (!projectId) return;
    setWorkPapersLoading(true);
    try {
      const blob = await projectService.exportWorkPapers(projectId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `WorkPapers_${projectId}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Export failed');
    } finally {
      setWorkPapersLoading(false);
    }
  };

  const handleUploadConfirm = async () => {
    if (!uploadFile || !projectId || !activeCalcId) return;
    setUploading(true);
    try {
      const result = await workspaceService.uploadData(projectId, activeCalcId, selectedSheetIndex, uploadFile);
      message.success(`${result.rowsImported} rows imported`);
      setUploadModalOpen(false);
      setUploadFile(null);
      await fetchWorkspace();
    } catch {
      message.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleGridReady = (e: GridReadyEvent) => {
    gridApiRef.current = e.api;
  };

  if (loading) {
    return (
      <div className={styles.pageWrap} style={{ alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (workspaceError) {
    return (
      <div className={styles.pageWrap} style={{ alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        <div style={{ color: '#ef4444', fontSize: 14 }}>Failed to load workspace. Please try again.</div>
        <button className={styles.btnSecondary} onClick={fetchWorkspace}>Retry</button>
      </div>
    );
  }

  const sheets = activeCalc?.sheets ?? [];
  const outputSheets = sheets.filter((s) => isOutputSheet(s));
  const calcSheets = sheets.filter((s) => isCalcSheet(s));
  const inputSheets = sheets.filter((s) => !isOutputSheet(s) && !isCalcSheet(s));

  const renderSheetSidebarItem = (sheet: WorkspaceSheet, onSelectExtra?: () => void) => {
    const isOut = isOutputSheet(sheet);
    const isCalc = !isOut && isCalcSheet(sheet);
    return (
      <button
        key={sheet.sheetIndex}
        className={[
          styles.sheetSidebarItem,
          sheet.sheetIndex === selectedSheetIndex ? styles.sheetSidebarItemActive : '',
        ].filter(Boolean).join(' ')}
        onClick={() => { handleSheetTabSelect(sheet.sheetIndex); onSelectExtra?.(); }}
      >
        {isOut ? (
          <span className={styles.sheetBadgeOutput}>OUTPUT</span>
        ) : isCalc ? (
          <span className={styles.sheetBadgeCalcChain}>CALC</span>
        ) : null}
        <span className={styles.sheetSidebarItemName}>{sheet.sheetName.replace(/_/g, ' ')}</span>
      </button>
    );
  };

  const renderSheetGroups = (onSelectExtra?: () => void) => (
    <>
      {inputSheets.length > 0 && (
        <>
          <div className={styles.sheetGroupLabel}>Input Sheets</div>
          {inputSheets.map((sheet) => renderSheetSidebarItem(sheet, onSelectExtra))}
        </>
      )}
      {calcSheets.length > 0 && (
        <>
          <div className={styles.sheetGroupLabelCalc}>Calculation Chain</div>
          {calcSheets.map((sheet) => renderSheetSidebarItem(sheet, onSelectExtra))}
        </>
      )}
      {outputSheets.length > 0 && (
        <>
          <div className={styles.sheetGroupLabel}>Output Sheets</div>
          {outputSheets.map((sheet) => renderSheetSidebarItem(sheet, onSelectExtra))}
        </>
      )}
    </>
  );

  const renderCalcListItems = (onSelectExtra?: () => void) => (
    (workspace?.calculations ?? []).length === 0 ? (
      <div style={{ padding: '16px 8px', fontSize: 12, color: 'rgba(255,255,255,0.30)' }}>
        No calculations configured
      </div>
    ) : (
      workspace!.calculations.map((calc, idx) => (
        <div
          key={calc.calcProfileItemId}
          className={`${styles.calcItem}${calc.calcProfileItemId === activeCalcId ? ` ${styles.calcItemActive}` : ''}`}
          onClick={() => { handleCalcSelect(calc); onSelectExtra?.(); }}
        >
          <div className={styles.calcNum}>{idx + 1}</div>
          <div className={styles.calcInfo}>
            <div className={styles.calcItemName}>{calc.standardCalculationName}</div>
            {(calc.category || calc.standardCalculationCode) && (
              <div className={styles.calcItemCode}>
                {calc.category ?? calc.standardCalculationCode}
              </div>
            )}
          </div>
          <div className={
            calc.status === 'Completed' ? styles.dotComplete : styles.dotPending
          } />
        </div>
      ))
    )
  );

  return (
    <div className={styles.pageWrap}>
      {/* Top bar */}
      <div className={styles.topBar}>
        <button className={styles.breadcrumb} onClick={() => go('/calculations/project')}>
          Projects
        </button>
        <span className={styles.breadcrumbSep}>›</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span className={styles.projectName}>{workspace?.projectName ?? '—'}</span>
          {workspace && workspace.partnershipName && (
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', lineHeight: 1 }}>
              {workspace.calculations.length} calculation{workspace.calculations.length !== 1 ? 's' : ''} from {workspace.partnershipName} {workspace.taxYear} profile
            </span>
          )}
        </div>
        {workspace?.status && (
          <span className={styles.statusBadge}>{workspace.status}</span>
        )}
        <button
          className={styles.btnRun}
          onClick={handleRunProject}
          disabled={isRunning || (!!readiness && !readiness.isReadyToRun)}
          title={readiness && !readiness.isReadyToRun ? 'Resolve the issues below before running' : undefined}
        >
          <PlayCircleOutlined />
          {isRunning ? 'Running...' : 'Run Project'}
        </button>
      </div>

      {/* Readiness panel */}
      {!readinessLoading && readiness && (
        <div style={{ padding: '10px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          {readiness.isReadyToRun ? (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 12, fontWeight: 600, color: '#22c55e',
              background: 'rgba(34,197,94,0.12)', padding: '4px 10px', borderRadius: 6,
            }}>
              ✓ Ready to run
            </span>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {readiness.checks.filter((c) => c.status === 'fail').map((c) => (
                <div key={c.name} style={{ fontSize: 12, color: '#ef4444' }}>
                  {renderCheckDetail('❌', c.detail)}
                </div>
              ))}
              {readiness.checks.filter((c) => c.status === 'warn').map((c) => (
                <div key={c.name} style={{ fontSize: 12, color: '#eab308' }}>
                  {renderCheckDetail('⚠', c.detail)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Body */}
      <div className={styles.body}>
        {/* Left panel */}
        <div
          className={`${styles.leftPanelWrapper}${calcPanelCollapsed ? ` ${styles.leftPanelWrapperCollapsed}` : ''}`}
          onMouseEnter={() => calcPanelCollapsed && setCalcPanelHovered(true)}
          onMouseLeave={() => setCalcPanelHovered(false)}
        >
          <div className={styles.leftPanel}>
            <div className={styles.panelTitleRow}>
              {!calcPanelCollapsed && <div className={styles.panelTitle}>Calculations</div>}
              <button
                onClick={toggleCalcPanel}
                className={styles.sidebarToggle}
                title={calcPanelCollapsed ? 'Expand calculations (Shift+[)' : 'Collapse calculations (Shift+[)'}
                aria-label={calcPanelCollapsed ? 'Expand calculations panel' : 'Collapse calculations panel'}
              >
                {calcPanelCollapsed ? '›' : '‹'}
              </button>
            </div>

            {calcPanelCollapsed ? (
              <div className={styles.collapsedCalcIndicators}>
                {(workspace?.calculations ?? []).map((calc, idx) => (
                  <div
                    key={calc.calcProfileItemId}
                    className={`${styles.collapsedCalcBadge}${calc.calcProfileItemId === activeCalcId ? ` ${styles.collapsedCalcBadgeActive}` : ''}`}
                    title={calc.standardCalculationName}
                    onClick={() => handleCalcSelect(calc)}
                  >
                    {idx + 1}
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.calcList}>{renderCalcListItems()}</div>
            )}
          </div>

          {calcPanelCollapsed && (
            <div className={`${styles.calcPanelOverlay}${calcPanelHovered ? ` ${styles.calcPanelOverlayHovered}` : ''}`}>
              <div className={styles.panelTitle}>Calculations</div>
              <div className={styles.calcList}>
                {renderCalcListItems(() => setCalcPanelHovered(false))}
              </div>
            </div>
          )}
        </div>

        {/* Main area */}
        <div
          className={styles.mainArea}
          style={{ opacity: calcFading ? 0 : 1, transition: 'opacity 0.15s ease-in-out' }}
        >
          {!activeCalc ? (
            <div className={styles.noCalcSelected}>
              Select a calculation to enter data
            </div>
          ) : (
            <>
              <div className={styles.tableHeader}>
                <div className={styles.calcTitle}>{activeCalc.standardCalculationName}</div>
                {activeCalc.templateName && (
                  <div className={styles.calcMeta}>{activeCalc.templateName}</div>
                )}
              </div>

              <div className={styles.workspaceBody}>
                {/* Sheet sidebar */}
                {sheets.length > 0 && (
                  <div
                    className={`${styles.sheetSidebarWrapper}${sidebarCollapsed ? ` ${styles.sheetSidebarWrapperCollapsed}` : ''}`}
                    onMouseEnter={() => sidebarCollapsed && setSidebarHovered(true)}
                    onMouseLeave={() => setSidebarHovered(false)}
                  >
                    <div className={`${styles.sheetSidebar}${sidebarCollapsed ? ` ${styles.sheetSidebarCollapsed}` : ''}`}>
                      <button
                        onClick={toggleSidebar}
                        className={styles.sidebarToggle}
                        title={sidebarCollapsed ? 'Expand sidebar ([)' : 'Collapse sidebar ([)'}
                        aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                      >
                        {sidebarCollapsed ? '›' : '‹'}
                      </button>
                      {sidebarCollapsed ? (
                        <div className={styles.collapsedSheetIndicators}>
                          {sheets.map((sheet) => (
                            <div
                              key={sheet.sheetIndex}
                              className={styles.collapsedSheetDot}
                              title={sheet.sheetName.replace(/_/g, ' ')}
                              onClick={() => handleSheetTabSelect(sheet.sheetIndex)}
                              style={{
                                backgroundColor: sheet.sheetIndex === selectedSheetIndex
                                  ? '#F26522'
                                  : 'rgba(255,255,255,0.2)',
                              }}
                            />
                          ))}
                        </div>
                      ) : (
                        renderSheetGroups()
                      )}
                    </div>

                    {sidebarCollapsed && (
                      <div className={`${styles.sheetSidebarOverlay}${sidebarHovered ? ` ${styles.sheetSidebarOverlayHovered}` : ''}`}>
                        {renderSheetGroups(() => setSidebarHovered(false))}
                      </div>
                    )}
                  </div>
                )}

                <div className={styles.gridColumn}>
                  <div className={styles.gridWrapper} style={{ height: '100%' }}>
                    {!activeSheet || colDefs.length === 0 ? (
                      <div className={styles.emptyState}>
                        {activeSheetIsOutput ? (
                          <>
                            <div className={styles.emptyIcon}>⚡</div>
                            <div className={styles.emptyText}>Run calculation to see results</div>
                            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)' }}>
                              Enter input data and click Run Project
                            </div>
                          </>
                        ) : (
                          <>
                            <div className={styles.emptyIcon}>📋</div>
                            <div className={styles.emptyText}>
                              No template assigned to this calculation
                            </div>
                            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)' }}>
                              Assign a template in Configurations → Calc Profile to define columns
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <AgGridReact
                        ref={gridRef}
                        rowData={gridRows}
                        columnDefs={colDefs}
                        defaultColDef={workspaceDefaultColDef}
                        rowHeight={36}
                        headerHeight={40}
                        onGridReady={handleGridReady}
                        onCellValueChanged={activeSheetIsOutput ? undefined : handleCellValueChanged}
                        domLayout="normal"
                        suppressMovableColumns
                        suppressRowClickSelection
                        stopEditingWhenCellsLoseFocus
                        tooltipShowDelay={300}
                        overlayNoRowsTemplate={
                          activeSheetIsOutput ? outputNoRowsOverlay : activeSheetIsCalc ? calcNoRowsOverlay : noRowsOverlay
                        }
                        theme={activeSheetIsOutput ? outputGridTheme : activeSheetIsCalc ? calcGridTheme : gridTheme}
                        sideBar={true}
                        cellSelection={true}
                        enableCellTextSelection={true}
                        rowGroupPanelShow="always"
                        groupDisplayType="multipleColumns"
                        suppressMenuHide={false}
                        statusBar={workspaceStatusBar}
                      />
                    )}
                  </div>

                  <div className={styles.tableActions}>
                    {activeSheet && activeSheet.columns.length > 0 && activeSheet.isInput && (
                      <button className={styles.btnAddRow} onClick={handleAddRow}>
                        <PlusOutlined style={{ fontSize: 11, marginRight: 4 }} />
                        Add Row
                      </button>
                    )}
                    <button className={styles.btnSecondary} onClick={handleDownloadTemplate}>
                      Download Template
                    </button>
                    {activeSheet?.isInput && (
                      <button className={styles.btnSecondary} onClick={handleUploadClick}>
                        Upload Data
                      </button>
                    )}
                    <button
                      className={styles.btnSecondary}
                      onClick={handleDownloadWorkPapers}
                      disabled={workPapersLoading}
                    >
                      {workPapersLoading ? 'Preparing work papers...' : 'Download Work Papers'}
                    </button>
                    {calculating && (
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginLeft: 4 }}>
                        Calculating...
                      </span>
                    )}
                    {activeSheet?.isInput && (
                      <button
                        className={styles.btnSave}
                        onClick={handleSave}
                        disabled={saving || !activeSheet || activeSheet.columns.length === 0}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Progress Modal */}
      <Modal
        title={null}
        open={showProgressModal}
        footer={null}
        closable={false}
        width={420}
        styles={{ content: { background: '#1a2744', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12 }, body: { background: '#1a2744', padding: 28 } }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#e2e8f0', marginBottom: 8 }}>
            Running Calculations
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.50)', marginBottom: 20 }}>
            {isRunning
              ? `${completedJobs} of ${totalJobs || '...'} complete`
              : runSuccess
              ? 'All calculations complete'
              : runError ?? ''}
          </div>
          {pollingError && (
            <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 16 }}>
              Lost connection while checking job status — progress shown may be stale.
            </div>
          )}
          {totalJobs > 0 && (
            <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 6, height: 8, overflow: 'hidden', marginBottom: 20 }}>
              <div
                style={{
                  height: '100%',
                  width: `${totalJobs > 0 ? Math.round((completedJobs / totalJobs) * 100) : 0}%`,
                  background: runError ? '#ef4444' : runSuccess ? '#22c55e' : '#F26522',
                  borderRadius: 6,
                  transition: 'width 0.4s ease',
                }}
              />
            </div>
          )}
          {isRunning && (
            <Spin style={{ marginBottom: 20 }} />
          )}
          {runSuccess && (
            <div style={{ color: '#22c55e', fontSize: 13, marginBottom: 20 }}>✓ All calculations complete</div>
          )}
          {runError && (
            <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 20 }}>{runError}</div>
          )}
          {failedJobs.length > 0 && (
            <div
              style={{
                textAlign: 'left',
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.25)',
                borderRadius: 6,
                padding: 12,
                marginBottom: 20,
                maxHeight: 180,
                overflowY: 'auto',
              }}
            >
              <div style={{ color: '#ef4444', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                ⚠️ {failedJobs.length} job{failedJobs.length > 1 ? 's' : ''} failed
              </div>
              {failedJobs.map((j) => (
                <div key={j.id} style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, marginBottom: 4 }}>
                  Entity: {j.entityId ?? 'Unknown'} — {j.errorMessage || `Status: ${j.status}`}
                </div>
              ))}
            </div>
          )}
          {!isRunning && (
            <button
              onClick={() => { setShowProgressModal(false); setRunSuccess(false); setRunError(null); setFailedJobs([]); }}
              style={{ background: '#F26522', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 24px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
            >
              Close
            </button>
          )}
        </div>
      </Modal>

      {/* Upload Modal */}
      <Modal
        title="Upload Data"
        open={uploadModalOpen}
        onCancel={() => { setUploadModalOpen(false); setUploadFile(null); }}
        onOk={handleUploadConfirm}
        okText={uploading ? 'Importing...' : 'Import'}
        confirmLoading={uploading}
        okButtonProps={{ disabled: !uploadFile }}
        styles={{ body: { background: '#1a1a2e' } }}
      >
        <Upload.Dragger
          accept=".xlsx"
          multiple={false}
          beforeUpload={(file) => { setUploadFile(file); return false; }}
          onRemove={() => setUploadFile(null)}
          fileList={uploadFile ? [{ uid: '1', name: uploadFile.name, status: 'done' }] : []}
          style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.15)' }}
        >
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p style={{ color: 'rgba(255,255,255,0.65)' }}>Click or drag an .xlsx file here</p>
        </Upload.Dragger>
        {uploadFile && (
          <div style={{ marginTop: 12, fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
            Selected: {uploadFile.name}
          </div>
        )}
      </Modal>
    </div>
  );
};
